import React from 'react';
import PropTypes from 'prop-types';
import { isEqual, head, includes, noop, filter, find, debounce, set, isEmpty, get, last, findIndex, findLastIndex, isUndefined, map, every, cloneDeep, remove, size, indexOf } from 'lodash';
import scrollIntoView from 'scroll-into-view';
import escapeStringRegexp from 'escape-string-regexp';

// local dependencies
// import { choiceList as ChoiceListView } from './choice-list.view';
import { choiceListResizable as ChoiceListResizable } from './choice-resizable-list.view';
import { isEnterKey, isArrowDownKey, isArrowUpKey } from 'utils/keyChecker';
import { getLabel } from 'utils/getLabel';
import { SELECT_ALL_OPTION_VALUE, DATA_TYPES } from 'constants/common';
import { messageBus } from 'core/modules/message-bus';
import * as MESSAGE_BUS_EVENTS from 'constants/message-bus-events';

/**
 * @type {object}
 * @desc A React presentation/dumb view component.
 * @example
 * import { choiceList as ChoiceList } from 'modules/choice-list';
 *
 * // render
 * <ChoiceList propName='propValue'/>
 */
export class ChoiceList extends React.Component {
    constructor( props ) {
        super( props );

        // view ref
        this.viewRef = React.createRef();

        // options
        const options = this.composeOptionsFromProps( props );
        
        // get selected values
        let selected = props.selected;
        if( props.displaySelectAllOption && includes( selected, SELECT_ALL_OPTION_VALUE ) ) {
            if( includes( [ DATA_TYPES.DEEP_SELECT, DATA_TYPES.DEEP_MULTI_SELECT ], props.type ) ) {
                selected = options.map( _opt => ( { id: _opt[ `${this.props.selectionCriteria}` ], selection: map( _opt.options, `${ this.props.selectionCriteria }` ) } ) ); // add everything to the selection
            } else {
                selected = map( filter( options, _option => ! isEmpty( _option[ `${this.props.selectionCriteria}` ] ) ), `${ this.props.selectionCriteria }` ); // select all options when `selectAll` option is selected
            }
        }

        // component static data
        this.data = {
            options: options,
        };

        // component state
        this.state = {
            options,
            selected,
            activeIndex: undefined,
            activeOrderIndex: undefined,
            queryString: this.props.queryString,
            isFetching: false,
            isFetchingMore: false
        };

        // bind events and methods
        this.__bind();

    }

    // bind methods and add event listeners
    __bind() {
        this.handleOptionSelect = this.handleOptionSelect.bind( this );
        this.isOptionSelected = this.isOptionSelected.bind( this );
		this.handleKeyDown = debounce( this.handleKeyDown, 100 ).bind( this );
        this.handleQueryChange = debounce( this.handleQueryChange.bind( this ), 100 );
        this.wipeData = this.wipeData.bind( this );
        this.updateOption = this.updateOption.bind( this );
        this.handleListPagination = debounce( this.handleListPagination.bind( this ), 100 );

        // listen to keydown event
        window.addEventListener( 'keydown', this.handleKeyDown );
        messageBus.on( `${MESSAGE_BUS_EVENTS.CHOIOCE_LIST_UPDATED}_${this.props.name}`, this.updateOption, this );

    }

    // remove event listeners
    __unbind() {
        window.removeEventListener( 'keydown', this.handleKeyDown );

        messageBus.off( `${MESSAGE_BUS_EVENTS.CHOIOCE_LIST_UPDATED}_${this.props.name}`, this.updateOption, this );
    }
    
    /***************************************/

    /**
     * @desc Compose option from the props.
     */
	composeOptionsFromProps( props ) {
        log.debug( 'ChoiceList.composeOptionsFromProps()' );
        
        // clone options received in props
        let options = cloneDeep( props.options );

        // add options in case of dynamicall selected options
        if( 0 !== size( get( props, 'dynamicSelectedOptions', [] ) ) ) {
            options = props.dynamicSelectedOptions;
        }

        // add `select all` option
        if( props.displaySelectAllOption & ! isEmpty( options ) ) {
            options.unshift( { name: SELECT_ALL_OPTION_VALUE, title: { refId: '', value: 'All' }, value: SELECT_ALL_OPTION_VALUE } );
        }

        // add index
		return map( options, ( option, index ) => {
            return {
                ...option,
                index: index + 1,
            };
        } );
	}

    /**
     * @desc Get selected values.
     * @param {*} option - selected option
     */
    getSelection( option, subOption ) {
        log.debug( 'ChoiceList.getSelection()' );

        if( includes( [ DATA_TYPES.DEEP_SELECT, DATA_TYPES.DEEP_MULTI_SELECT ], this.props.type ) ) {
            
            // new selected value
            const selection = cloneDeep( this.state.selected );
            
            const selectableOptions = filter( this.data.options, _option => ! isEmpty( _option[ `${this.props.selectionCriteria}` ] ) ); // `labels` excluded
            const selectableOptionsExceptSelectAll = this.props.displaySelectAllOption ? filter( selectableOptions, _option => _option[ `${this.props.selectionCriteria}` ] !== SELECT_ALL_OPTION_VALUE ) : selectableOptions; // remove `selectAll` option

            /*--------*/

            // handle select all option
            if( option[ `${this.props.selectionCriteria}` ] === SELECT_ALL_OPTION_VALUE ) {
                if( find( selection, ( _val ) => _val.id === SELECT_ALL_OPTION_VALUE ) ) {
                    return []; // wipe selection
                } else {
                    return this.data.options.map( _opt => ( { id: _opt[ `${this.props.selectionCriteria}` ], selection: map( _opt.options, `${ this.props.selectionCriteria }` ) } ) ); // add everything to the selection
                }
            }

            /*--------*/

            // get selected group option and initialize if doesn't exist
            let selectedGroup = find( selection, ( _val ) => _val.id === option[ `${this.props.selectionCriteria}` ] );

            // add/remove/update `selectedGroup` based on `option` and `subOption`
            if( ! isEmpty( subOption ) ) {

                // initialize empty `selectedGroup` if not already exist
                if( isEmpty( selectedGroup ) ) {
                    selectedGroup = { id: option[ `${this.props.selectionCriteria}` ], selection: [] };
                    selection.push( selectedGroup );
                }

                // get sub-option selected from the `selectedGroup`
                const subOptionSelection = selectedGroup.selection;

                // check if `subOption` is already selected
                if( includes( subOptionSelection, subOption[ `${this.props.selectionCriteria}` ] ) ) {
                    remove( subOptionSelection, _v => _v === subOption[ `${this.props.selectionCriteria}` ] );

                    // remove select all option as well
                    remove( selection, ( _val ) => _val.id === SELECT_ALL_OPTION_VALUE );

                    // if all sub-options are removed, removed the group
                    if( isEmpty( subOptionSelection ) ) {
                        remove( selection, ( _val ) => _val.id === option[ `${this.props.selectionCriteria}` ] );
                    }
                } else {
                    subOptionSelection.push( subOption[ `${this.props.selectionCriteria}` ] );
                }

            } else {

                // initialize `selectedGroup` if not already exist
                if( isEmpty( selectedGroup ) ) {
                    selection.push( { id: option[ `${this.props.selectionCriteria}` ], selection: map( option.options, `${ this.props.selectionCriteria }` ) } ); // add empty group with all sub-options
                } else {
                    remove( selection, ( _val ) => _val.id === option[ `${this.props.selectionCriteria}` ] ); // remove group

                    // remove select all option as well
                    remove( selection, ( _val ) => _val.id === SELECT_ALL_OPTION_VALUE );
                }
            }

            // if all options are selected, also select `selectAll` option
            if( this.props.displaySelectAllOption && ! find( selection, ( _val ) => _val.id === SELECT_ALL_OPTION_VALUE ) ) {
                
                // check if all options and sub-options are selected
                const areAllOptionsSelected = every( selectableOptionsExceptSelectAll, ( _option ) => {
                    const _selectedGroup = find( selection, ( _val ) => _val.id === _option[ `${this.props.selectionCriteria}` ] );
                    if( _selectedGroup ) {
                        return every( _option.options, _subOption => includes( _selectedGroup.selection, _subOption[ `${this.props.selectionCriteria}` ] ) );
                    }

                    return false;
                } );

                // if all options are selected, push select-all option in the selection
                if( areAllOptionsSelected ) {
                    selection.push( { id: SELECT_ALL_OPTION_VALUE, selection: [] } );
                }
            }

            return selection;

        } else if( includes( [ DATA_TYPES.MULTI_SELECT ], this.props.type ) ) {

            // new selected value
            let selection = [];
            const selectableOptions = filter( this.state.options, _option => ! isEmpty( _option[ `${this.props.selectionCriteria}` ] ) ); // `labels` excluded
            const selectableOptionsExceptSelectAll = this.props.displaySelectAllOption ? filter( selectableOptions, _option => _option[ `${this.props.selectionCriteria}` ] !== SELECT_ALL_OPTION_VALUE ) : selectableOptions; // remove `selectAll` option

            if( includes( this.state.selected, option[ `${this.props.selectionCriteria}` ] ) ) {
                if( this.props.displaySelectAllOption && option[ `${this.props.selectionCriteria}` ] === SELECT_ALL_OPTION_VALUE ) {
                    selection = []; // wipe all selection when `selectAll` option is unselected
                } else {
                    selection = filter( this.state.selected, _value => {
                        return _value !== option[ `${this.props.selectionCriteria}` ] && _value !== SELECT_ALL_OPTION_VALUE; // remove `selectAll` option as well
                    } );
                }
            } else {
                if( this.props.displaySelectAllOption && option[ `${this.props.selectionCriteria}` ] === SELECT_ALL_OPTION_VALUE ) {
                    selection = map( selectableOptions, 'value' ); // select all options when `selectAll` option is selected
                } else {
                    selection = this.state.selected.concat( option[ `${this.props.selectionCriteria}` ] );
                }
            }

            // if all options are selected, also select `selectAll` option
            if( this.props.displaySelectAllOption && ! includes( selection, SELECT_ALL_OPTION_VALUE ) ) {
                const areAllOptionsSelected = every( selectableOptionsExceptSelectAll, ( _option ) => includes( selection, _option[ `${this.props.selectionCriteria}` ] ) );

                if( areAllOptionsSelected ) {
                    selection = map( selectableOptions, `${ this.props.selectionCriteria }` );
                }
            }

            return selection;

        } else {
            return option[ `${this.props.selectionCriteria}` ];
        }
    }

    handleListPagination() {
        log.debug( 'ChoiceList.handleListPagination()' );

        if( ! this.props.disablePagination && ! this.state.isFetchingMore ) {

            this.setState( { isFetchingMore: true }, async () => {
                this.props.onPaginationCallBack( { query: this.state.queryString } );
            } );

        }
    }

    /**
     * @desc Handle local filter (search) query change.
     */
	handleQueryChange( query = '' ) {
        log.debug( 'ChoiceList.handleQueryChange()' );
        
        if( get( this.props, 'allowSearch', false ) ) {

            if( isEmpty( query ) ) {
                this.setState( {
                    queryString: '',
                    options: [],
                    activeIndex: undefined,
                } );
            } else {
                
                this.setState( {
                    queryString: query,
                    options: [],
                    activeIndex: undefined,
                    isFetching: true
                }, async () => {

                    const {  options, status= 'cancelled' } = await this.props.handleQueryChange( { query } );
                    'cancelled' === status ? null : this.setState( { options, isFetching: false } );

                } );
            }

        } else {

            // clone master options
            const _masterOptions = cloneDeep( this.data.options );

            // if `query` is empty, reset to the master options
            if( isEmpty( query ) ) {
                this.setState( {
                    queryString: '',
                    options: _masterOptions,
                    activeIndex: undefined,
                } );
            } else {

                // get regular expression for match
                const regExp = new RegExp( escapeStringRegexp( query ), 'gi' );

                // transform master options based on match
                const filteredOptions = map( _masterOptions, ( item ) => {

                    // if parent option matches, return option back
                    // if( regExp.match( getLabel( item.title ) ) ) {
                    //     return item;
                    // }

                if( getLabel( item.title ).match( regExp ) ) {
                    return item;
                }

                // check if sub-options matches
                if( ! isEmpty( item.options ) ) {
                    item.options = map( item.options, ( subItem ) => {
                
                        // if parent option matches, return option back
                        if( regExp.test( getLabel( subItem.title ) ) ) {
                            return subItem;
                        }

                        // return `null` for no option match
                        return null;
                    } );

                    // filter out `null` non-matched options
                    item.options = filter( item.options, Boolean );

                    // return option if any sub-option has a match
                    if( ! isEmpty( item.options ) ) {
                        return item;
                    }
                }

                    // return `null` for no option match
                    return null;
                } );

                this.setState( {
                    queryString: query,
                    options: filter( filteredOptions, Boolean ), // filter out `null` non-matched options
                    activeIndex: undefined,
                } );
            }
        }
	}

    /**
     * @desc On an option select, update state with selected values and active option index.
     * @param {*} option - selected option
     */
    handleOptionSelect( option, subOption ) {
		log.debug( 'ChoiceList.handleOptionSelect()' );

		const selected = this.getSelection( option, subOption );
        const options = this.state.options;
        
        // keep the popover open in case user selects the same option again
        if( this.state.selected !== get( option, `${ this.props.selectionCriteria }`, '' ) ) {
            this.setState( {
                activeIndex: get( find( options, { [ `${ this.props.selectionCriteria }` ]: option[ `${this.props.selectionCriteria}` ] } ), 'index' ),
                selected: selected,
                options
            }, () => {

                const selectedList = filter( cloneDeep( this.state.options ), _option => {
                    return -1 !== indexOf( _option[ this.state.selected, `${this.props.selectionCriteria}` ] );
                } );

                this.executeCallback( this.state.selected, selectedList ); // execute callback
    
                // close close popover for single select
                if( this.props.type === DATA_TYPES.SINGLE_SELECT ) {
                    this.props.closePopover();
                }
            } );
        }
    }

    /**
     * @desc Check if given option is selected
     */
    isOptionSelected( option, subOption ) {
        
        if( this.props.type === DATA_TYPES.SINGLE_SELECT ) {
            return this.state.selected === option[ `${this.props.selectionCriteria}` ];
        } else if( this.props.type === DATA_TYPES.MULTI_SELECT ) {
            return includes( this.state.selected, option[ `${this.props.selectionCriteria}` ] );
        } else if( includes( [ DATA_TYPES.DEEP_SELECT, DATA_TYPES.DEEP_MULTI_SELECT ], this.props.type ) ) {
            
            // get selected group option and initialize if doesn't exist
            const selectedGroup = find( this.state.selected, ( _val ) => _val.id === option[ `${this.props.selectionCriteria}` ] );
            
            
            // if `selectedGroup` is missing, option is not selected
            if( isEmpty( selectedGroup ) ) {
                return false;
            }

            // if `subOption` is not empty, check for sub-option value in group
            if( ! isEmpty( subOption ) ) {
                return includes( selectedGroup.selection, subOption[ `${this.props.selectionCriteria}` ] );
            } else {

                // to show the dah [-] if not all sub options are selected
                const allOptionsOfSelectedGroup = find( this.state.options, ( _val ) => _val[ `${this.props.selectionCriteria}` ] === selectedGroup.id );
                const customizedSelectedOptions = [];
                customizedSelectedOptions.push( { id: allOptionsOfSelectedGroup[ `${this.props.selectionCriteria}` ], selection: map( allOptionsOfSelectedGroup.options, `${ this.props.selectionCriteria }` ) } );
            
                if( ( isEqual( option[ `${this.props.selectionCriteria}` ], SELECT_ALL_OPTION_VALUE ) ) ) {
                    return true;
                }else if( ! isEqual( head( customizedSelectedOptions ).selection.sort(), find( this.state.selected, ( _val ) => _val.id === customizedSelectedOptions[ 0 ].id ).selection.sort() ) ) {
                    return undefined;
                } else{
                    return true;
                }
            }
        }
    }

    /**
     * @desc On key-down, change active option.
     */
    handleKeyDown( event ) {
        log.debug( 'ChoiceList.handleKeyDownEvent()' );

        if ( isEnterKey( event.key ) ) {
            this.selectOptionOnEnterKey();
        } else if ( isArrowDownKey( event.key ) || isArrowUpKey( event.key ) ) {
            this.focusOptionOnArrowKey( event );
        }
    }

    /**
     * @desc On arrow button press, focus an option
     * Moved this logic here from arrowSelection Hoc based on discussion with Uday to resolve activeIndex problem.
     */
    focusOptionOnArrowKey( event ) {
        log.debug( 'ChoiceList.focusOptionOnArrowKey()' );

        event.preventDefault(); // stop scrolling
        event.stopPropagation(); // stop bubbling

        // check if key is up
        const isUpKey = isArrowUpKey( event.key );

        // current active option index
        let activeIndex = this.state.activeIndex;

        // set new active index
        if ( isUpKey ) {
            if( activeIndex === undefined ) {

                // if the very first key pressed is up arrow take the user to last selectable option
                const _activeIndex = findLastIndex( this.state.options, ( option ) => ( !isUndefined( option.index ) ) );
                activeIndex = this.state.options[ _activeIndex ].index;

                //activeIndex = get( last( this.state.options ), 'index' );
            } else if ( this.state.options[ findIndex( this.state.options, ( option ) => ( !isUndefined( option.index ) ) ) ].index === this.state.activeIndex ) {

                // if the activeIndex is first option and then up arrow is pressed then move the activeIndex to last selectable option
                const _activeIndex = findLastIndex( this.state.options, ( option ) => ( !isUndefined( option.index ) ) );
                activeIndex = this.state.options[ _activeIndex ].index;

                //activeIndex = get( last( this.state.options ), 'index' );
            } else {

                // if the prevOption is a label which is not selectable decrement the index by -2.
                const currentOrderIndex = findIndex( this.state.options, ( option ) => option.index === this.state.activeIndex );
                let preOption = this.state.options[ currentOrderIndex - 1 ];
                if( isUndefined( preOption.index ) ) {
                    preOption = this.state.options[ currentOrderIndex - 2 ];
                }
                activeIndex = preOption.index;
            }
        } else {
            if( activeIndex === undefined ) {

                // if the very first key pressed is down arrow take the user to first selectable option
                const _activeIndex = findIndex( this.state.options, ( option ) => ( !isUndefined( option.index ) ) );
                activeIndex = this.state.options[ _activeIndex ].index;

                //activeIndex = get( first( this.state.options ), 'index' );
            } else if ( this.state.activeIndex === get( last( this.state.options ), 'index' ) ) {

                // if the activeIndex is last option and then down arrow is pressed move the activeIndex to first selectable option
                const _activeIndex = findIndex( this.state.options, ( option ) => ( !isUndefined( option.index ) ) );
                activeIndex = this.state.options[ _activeIndex ].index;

               // activeIndex = get( first( this.state.options ), 'index' );
            } else {

                // if the nextOption is a label which is not selectable increment the index by +2.
                const currentOrderIndex = findIndex( this.state.options, ( option ) => option.index === this.state.activeIndex );
                let nextOption = this.state.options[ currentOrderIndex + 1 ];
                if( isUndefined( nextOption.index ) ) {
                    nextOption = this.state.options[ currentOrderIndex + 2 ];
                }
                activeIndex = nextOption.index;
            }
        }

        // get element by index
        const activeOrderIndex = findIndex( this.state.options, ( option ) => option.index === activeIndex );
        const optionsElem = this.viewRef.current ? this.viewRef.current.querySelector( '[data-elem-type="options-list"]' ) : null;
        const optionElem = optionsElem ? optionsElem.children[ activeOrderIndex ] : null;
        

        // update active index
        this.setState( {
            activeIndex
        }, () => {
            if( optionElem ) {
                scrollIntoView( optionElem, {
                    time: 100,
                    isScrollable: ( target ) => {
                        return target !== window;    // this is so that our window doesn't scroll while scrolling through a dropdown
                    }
                } );
            }
        } );
    }

    /**
     * @desc On Enter key press, select an option.
     */
    selectOptionOnEnterKey() {
        log.debug( 'ChoiceList.selectOptionOnEnterKey()' );

        if( undefined !== this.state.activeIndex ) {
            const option = find( this.state.options, { index: this.state.activeIndex } );

            // let the user select the option through enter key only when it is enabled
            if( !get( option, 'disabled', false ) ) {
                this.handleOptionSelect( option );
            }

            // close close popover for single select
            if( this.props.type === DATA_TYPES.SINGLE_SELECT ) {
                this.props.closePopover();
            }
        }
    }

    /**
     * @desc Wipe all selected options when clear is clicked and
     * call props.OnChange() to inform parent
     */
    wipeData() {
        log.debug( 'ChoiceList.wipeData()' );

        // clear selected data
        this.setState( {
            selected: [],
            activeIndex: undefined
        }, () => {

            // execute callback
            this.executeCallback( this.state.selected );

        } );

    }

    /**
     * @desc Execute parent `onChange` callback
     */
    executeCallback( selectedOptions, selectedList ) {
        log.debug( 'ChoiceList.executeCallback()' );
        
        // check if `selectedOptions` contains all `selectAll` option
        if( this.props.displaySelectAllOption ) {
            if( includes( [ DATA_TYPES.DEEP_SELECT, DATA_TYPES.DEEP_MULTI_SELECT ], this.props.type ) ) {
                if( find( selectedOptions, ( _val ) => _val.id === SELECT_ALL_OPTION_VALUE ) ) {
                    return this.props.onChange( [ SELECT_ALL_OPTION_VALUE ] ); // sending `['ALL']` has value only in all situations
                }
            } else if( includes( selectedOptions, SELECT_ALL_OPTION_VALUE ) ) {
                return this.props.onChange( [ SELECT_ALL_OPTION_VALUE ] ); // sending `['ALL']` has value only in all situations
            }
        }

        if( get( this.props, 'config.searchOnline', false ) ) {
            this.props.onChange( selectedOptions, selectedList );
        } else {
            this.props.onChange( selectedOptions );
        }
    }

    /**
        * @desc When location changes, update state and re-render the map
    */
    updateOption( { options } ) {
        log.debug( 'ChoiceList.updateOption()' );

        if( ! isEmpty( options ) )  {

            const newOptions = [ ...this.state.options, ...options ];
            set( this.data, 'options', newOptions );
            this.setState( { options: newOptions, isFetchingMore: false } );

        }
    }


    /***************************/

    /**
     * @desc Remove event listeners.
     */
    componentWillUnmount() {
        this.__unbind();
    }

    /**
     * @desc Render view.
     */
    render() {
        return (
            <ChoiceListResizable
                { ...this.state }
                ref={ this.viewRef }
                type={ this.props.type }
                options={ this.state.options }
                onOptionSelect={ this.handleOptionSelect }
                groupOptionsBy = { this.props.groupOptionsBy }
                choiceListClassName = { this.props.choiceListClassName }
                choiceListCheckboxClassName = { this.props.choiceListCheckboxClassName }
                width = { this.props.width }
                allowClear = { this.props.allowClear }
                allowFilter = { this.props.allowFilter }
                wipeData = { this.wipeData }
                enlargedTextWithNoSubOption = { this.props.enlargedTextWithNoSubOption }
                onQueryChange={ this.handleQueryChange }
                isOptionSelected={ this.isOptionSelected }
                hasRadioBox={ this.props.hasRadioBox }
                placeholder={ this.props.placeholder }
                resizableList={ this.props.resizableList }

                // pagination related props
                disablePagination={ this.props.disablePagination }
                fetchingType={ this.props.fetchingType }
                onPagination={ this.handleListPagination }
            />
        );
    }
}

// set display name
ChoiceList.displayName = 'ChoiceList';

// set default props
ChoiceList.defaultProps = {
    type: DATA_TYPES.SINGLE_SELECT,
    selected: undefined,
    options: [],
    onChange: noop,
	closePopover: noop,
    choiceListClassName: '',
	allowFilter: false,
    displaySelectAllOption: false,
    hasRadioBox: false,
    selectionCriteria: 'value',
    queryString: '',
    allowSearch: false,

    // pagination related props
    disablePagination: true,
    onPagination: noop,
    fetchingType: undefined,
    isPaginationFetching: false,
    resizableList: true
};

// prop types
ChoiceList.propTypes = {
    selected: PropTypes.oneOfType( [ PropTypes.any, PropTypes.array ] ), // selected value(s)
    options: PropTypes.array, // options
    onChange: PropTypes.func, // on selection change
	closePopover: PropTypes.func, // popover close method
    resizableList: PropTypes.bool
};
